import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { Shield, Upload, Check, X, Clock, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format, parse } from "date-fns";

const DOC_TYPES = [
  { v: "passport", l: "Passport" },
  { v: "id_card", l: "National ID Card" },
  { v: "driver_license", l: "Driver License" },
];

export default function KYC() {
  const { user } = useApp();
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    doc_type: "passport",
    doc_number: "",
    full_name: user?.name || "",
    dob: "",
    country: "HU",
    address: "",
    front_image_data_url: "",
    back_image_data_url: "",
    selfie_image_data_url: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/compliance/kyc/status"), api.get("/compliance/config")]);
      setStatus(s.data);
      setCfg(c.data);
    } catch {}
  };

  const readFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) { toast.error("File too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.front_image_data_url) { toast.error("Front image required"); return; }
    setBusy(true);
    try {
      await api.post("/compliance/kyc/submit", form);
      toast.success("KYC submitted for review");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const s = status?.status || "not_started";
  const badge = {
    not_started: { icon: Shield, txt: "Not started", cls: "text-white/60" },
    pending: { icon: Clock, txt: "Under Review", cls: "text-yellow-400" },
    approved: { icon: Check, txt: "Approved ✓", cls: "text-emerald-400" },
    rejected: { icon: X, txt: "Rejected", cls: "text-red-500" },
  }[s] || { icon: Shield, txt: s, cls: "text-white/60" };
  const Icon = badge.icon;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Identity Verification (KYC)</h1>
      </div>

      <div className="lux-card p-4 mb-6 flex items-center gap-3">
        <Icon className={badge.cls} size={22}/>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">Status</div>
          <div className={`font-bold ${badge.cls}`} data-testid="kyc-status">{badge.txt}</div>
        </div>
        {status?.submission?.notes && (
          <div className="ml-auto text-sm text-white/60 italic">Notes: {status.submission.notes}</div>
        )}
      </div>

      <div className="lux-card p-4 mb-6 text-sm bg-[#0e0e0e]">
        <div className="text-[#d4af37] font-bold mb-2">Why KYC?</div>
        <div className="text-white/70 leading-relaxed">
          KYC (Know Your Customer) verification is required by international gambling regulations. Once the casino is fully licensed,
          your verification will unlock cash-out functionality (currently pre-license mode: <strong>{cfg?.license_status || "pre_license"}</strong>).
          All data is encrypted and stored securely.
        </div>
      </div>

      {(s === "not_started" || s === "rejected") && (
        <div className="lux-card p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Document Type</label>
              <select data-testid="kyc-doctype" value={form.doc_type} onChange={e=>setForm({...form, doc_type: e.target.value})}
                      className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2">
                {DOC_TYPES.map(d=> <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Document Number</label>
              <input data-testid="kyc-docnum" value={form.doc_number} onChange={e=>setForm({...form, doc_number: e.target.value})}
                     className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Full Name</label>
              <input data-testid="kyc-fullname" value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})}
                     className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Date of Birth</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="kyc-dob-trigger" type="button"
                          className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-left flex items-center gap-2 hover:border-[#d4af37]/40">
                    <CalendarIcon size={14} className="text-white/40"/>
                    <span className={form.dob ? "" : "text-white/40"}>
                      {form.dob ? format(parse(form.dob, "yyyy-MM-dd", new Date()), "PPP") : "Select date..."}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto bg-[#0f0f0f] border border-white/10">
                  <Calendar
                    mode="single"
                    captionLayout="dropdown-buttons"
                    fromYear={1930}
                    toYear={new Date().getFullYear() - 18}
                    selected={form.dob ? parse(form.dob, "yyyy-MM-dd", new Date()) : undefined}
                    onSelect={(d)=>{
                      if (d) setForm({...form, dob: format(d, "yyyy-MM-dd")});
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Country (ISO 2)</label>
              <input data-testid="kyc-country" maxLength={2} value={form.country} onChange={e=>setForm({...form, country: e.target.value.toUpperCase()})}
                     className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 uppercase"/>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Address</label>
              <input data-testid="kyc-address" value={form.address} onChange={e=>setForm({...form, address: e.target.value})}
                     className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2"/>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { key: "front_image_data_url", label: "Front of Document *" },
              { key: "back_image_data_url", label: "Back (optional)" },
              { key: "selfie_image_data_url", label: "Selfie holding doc (optional)" },
            ].map(f=>(
              <div key={f.key}>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">{f.label}</label>
                <label className="cursor-pointer block border border-dashed border-white/20 rounded-lg p-4 text-center hover:border-[#d4af37]/40 transition">
                  <input data-testid={`kyc-file-${f.key}`} type="file" accept="image/*" className="hidden"
                         onChange={readFile((v)=>setForm(prev=>({...prev, [f.key]: v})))}/>
                  {form[f.key] ? (
                    <img src={form[f.key]} alt="" className="max-h-24 mx-auto rounded"/>
                  ) : (
                    <div className="text-white/40 text-xs flex flex-col items-center gap-2"><Upload size={20}/>Upload</div>
                  )}
                </label>
              </div>
            ))}
          </div>

          <button data-testid="kyc-submit" disabled={busy} onClick={submit}
                  className="btn-gold px-6 py-3 rounded-md text-sm uppercase tracking-widest">
            {busy ? "Submitting..." : "Submit for Review"}
          </button>
        </div>
      )}

      {(s === "pending" || s === "approved") && status?.submission && (
        <div className="lux-card p-6 text-sm">
          <div className="grid md:grid-cols-2 gap-3">
            <div><span className="text-white/40">Name:</span> {status.submission.full_name}</div>
            <div><span className="text-white/40">DOB:</span> {status.submission.dob}</div>
            <div><span className="text-white/40">Country:</span> {status.submission.country}</div>
            <div><span className="text-white/40">Doc:</span> {status.submission.doc_type} · {status.submission.doc_number || "—"}</div>
            <div className="md:col-span-2"><span className="text-white/40">Address:</span> {status.submission.address}</div>
            <div><span className="text-white/40">Submitted:</span> {new Date(status.submission.submitted_at).toLocaleString()}</div>
            {status.submission.reviewed_at && <div><span className="text-white/40">Reviewed:</span> {new Date(status.submission.reviewed_at).toLocaleString()}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
